import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Sparkles,
  X,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Database,
  ExternalLink,
  MessageSquare,
  ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { sendChatMessage, getSystemHealth, getConversationHistory } from '../services/api';

const STARTER_PROMPTS = [
  'What are your business operating hours?',
  'What enterprise SLA response times do you offer?',
  'How do you handle data security and backups?',
  'What AI and cloud platforms does Businex build?',
];

const FloatingAIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('businex_chat_messages');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // ignore parse error
      }
    }
    return [
      {
        id: 'welcome-1',
        sender: 'bot',
        text: 'Hello! I am the Businex AI Assistant, powered by Google Gemini and our ChromaDB RAG knowledge base. How can I help you today?',
        timestamp: new Date().toISOString(),
        sources: [],
      },
    ];
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return (
      localStorage.getItem('businex_chat_session_id') ||
      'usr-session-' + Math.random().toString(36).substring(2, 9)
    );
  });
  const [systemStatus, setSystemStatus] = useState('checking'); // 'ok', 'degraded', 'offline', 'checking'
  const [expandedSources, setExpandedSources] = useState({});
  const [copiedSession, setCopiedSession] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sync messages to localStorage
  useEffect(() => {
    localStorage.setItem('businex_chat_messages', JSON.stringify(messages));
  }, [messages]);

  // Sync sessionId to localStorage
  useEffect(() => {
    localStorage.setItem('businex_chat_session_id', sessionId);
  }, [sessionId]);

  // Check health on mount and periodically
  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      try {
        const health = await getSystemHealth();
        if (mounted) {
          setSystemStatus(health?.status === 'ok' ? 'ok' : 'degraded');
        }
      } catch (e) {
        if (mounted) {
          setSystemStatus('offline');
        }
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 45000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, loading]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [isOpen]);

  const toggleSource = (msgId) => {
    setExpandedSources((prev) => ({
      ...prev,
      [msgId]: !prev[msgId],
    }));
  };

  const handleCopySession = () => {
    navigator.clipboard.writeText(sessionId);
    setCopiedSession(true);
    setTimeout(() => setCopiedSession(false), 2000);
  };

  const handleNewSession = () => {
    const newId = 'usr-session-' + Math.random().toString(36).substring(2, 9);
    setSessionId(newId);
    setMessages([
      {
        id: 'welcome-' + Date.now(),
        sender: 'bot',
        text: 'Started a new conversation session. Feel free to ask any question about our services, SLAs, security, or technology stack!',
        timestamp: new Date().toISOString(),
        sources: [],
      },
    ]);
  };

  const handleLoadHistory = async () => {
    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const res = await getConversationHistory(sessionId);
      if (res && res.data) {
        setHistoryItems(res.data);
      } else {
        setHistoryItems([]);
      }
    } catch (err) {
      console.warn('Could not load session history:', err);
      setHistoryItems([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSendMessage = async (textToSend) => {
    const query = (textToSend || input).trim();
    if (!query || loading) return;

    const userMsgId = 'usr-' + Date.now();
    const newMsg = {
      id: userMsgId,
      sender: 'user',
      text: query,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, newMsg]);
    if (!textToSend) setInput('');
    setLoading(true);

    try {
      const response = await sendChatMessage({
        message: query,
        sessionId,
      });

      const replyData = response?.data;
      const botReply =
        replyData?.reply ||
        response?.reply ||
        'Thank you for your message. Our RAG engine processed your query successfully.';

      if (replyData?.sessionId && replyData.sessionId !== sessionId) {
        setSessionId(replyData.sessionId);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: 'bot-' + Date.now(),
          sender: 'bot',
          text: botReply,
          timestamp: replyData?.timestamp || new Date().toISOString(),
          sources: replyData?.sources || [],
          savedToDb: replyData?.savedToDb,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: 'err-' + Date.now(),
          sender: 'bot',
          isError: true,
          text: `I encountered an issue connecting to the AI backend: ${err.message}. Please ensure the server at http://localhost:3000 is active.`,
          timestamp: new Date().toISOString(),
          sources: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className="relative group flex items-center gap-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-3.5 sm:px-5 sm:py-3.5 rounded-full shadow-2xl hover:shadow-blue-500/40 transition-all border border-white/20"
          aria-label="Open Businex AI Assistant"
        >
          <div className="relative">
            <Bot size={24} className="text-white group-hover:rotate-12 transition-transform duration-300" />
            {/* Status dot indicator */}
            <span
              className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                systemStatus === 'ok'
                  ? 'bg-emerald-400 animate-pulse'
                  : systemStatus === 'degraded'
                  ? 'bg-amber-400'
                  : 'bg-rose-400'
              }`}
              title={`System Status: ${systemStatus}`}
            />
          </div>
          <span className="hidden sm:inline font-semibold text-sm tracking-wide">
            Ask Businex AI
          </span>
          <span className="hidden sm:flex items-center text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm">
            RAG v1.5
          </span>
        </motion.button>
      </div>

      {/* Floating Chat Drawer Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.92 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[94vw] sm:w-[420px] h-[580px] max-h-[82vh] bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-200/80 flex flex-col overflow-hidden text-slate-800"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white p-4 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center shadow-md">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm tracking-wide">Businex AI Studio</h3>
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        systemStatus === 'ok'
                          ? 'bg-emerald-400'
                          : systemStatus === 'degraded'
                          ? 'bg-amber-400'
                          : 'bg-rose-400'
                      }`}
                      title={`API Status: ${systemStatus}`}
                    />
                  </div>
                  <p className="text-[11px] text-slate-300 font-light flex items-center gap-1.5">
                    <span>Grounded with ChromaDB RAG</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {/* View Full Studio Link */}
                <Link
                  to="/ai-assistant"
                  onClick={() => setIsOpen(false)}
                  title="Open Full AI Studio"
                  className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <ExternalLink size={16} />
                </Link>

                {/* Reset / New Session */}
                <button
                  onClick={handleNewSession}
                  title="Start New Session"
                  className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <RefreshCw size={16} />
                </button>

                {/* Close */}
                <button
                  onClick={() => setIsOpen(false)}
                  title="Close Assistant"
                  className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Session Info Ribbon */}
            <div className="bg-slate-50 border-b border-slate-100 px-4 py-2 flex items-center justify-between text-[11px] text-slate-500">
              <div className="flex items-center gap-1.5 truncate max-w-[240px]">
                <span className="font-medium text-slate-400">Session:</span>
                <code className="text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-mono text-[10px] truncate">
                  {sessionId}
                </code>
                <button
                  onClick={handleCopySession}
                  className="text-slate-400 hover:text-slate-700 transition-colors p-0.5"
                  title="Copy session ID"
                >
                  {copiedSession ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                </button>
              </div>

              <button
                onClick={handleLoadHistory}
                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 hover:underline"
              >
                <MessageSquare size={12} />
                History
              </button>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm bg-gradient-to-b from-slate-50/50 to-white">
              {messages.map((msg) => {
                const isUser = msg.sender === 'user';
                const hasSources = msg.sources && msg.sources.length > 0;
                const isExpanded = expandedSources[msg.id];

                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[88%] p-3.5 rounded-2xl shadow-sm text-[13.5px] leading-relaxed break-words ${
                        isUser
                          ? 'bg-blue-600 text-white rounded-br-xs font-normal'
                          : msg.isError
                          ? 'bg-rose-50 border border-rose-200 text-rose-800 rounded-bl-xs'
                          : 'bg-white border border-slate-200/90 text-slate-800 rounded-bl-xs'
                      }`}
                    >
                      {msg.text}

                      {/* Grounding Sources Badge */}
                      {hasSources && (
                        <div className="mt-2.5 pt-2 border-t border-slate-100">
                          <button
                            onClick={() => toggleSource(msg.id)}
                            className="flex items-center justify-between w-full text-[11px] font-semibold text-blue-700 hover:text-blue-800 bg-blue-50/80 px-2.5 py-1 rounded-lg transition-colors"
                          >
                            <span className="flex items-center gap-1.5">
                              <Database size={12} className="text-blue-600" />
                              Grounding Sources ({msg.sources.length})
                            </span>
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>

                          {isExpanded && (
                            <div className="mt-2 space-y-1.5 text-[11px] bg-slate-50 p-2.5 rounded-lg border border-slate-200/60 max-h-40 overflow-y-auto">
                              {msg.sources.map((src, idx) => (
                                <div
                                  key={src.id || idx}
                                  className="border-b last:border-0 border-slate-200 pb-1.5 mb-1.5 last:pb-0 last:mb-0"
                                >
                                  <div className="flex items-center justify-between text-slate-500 font-mono text-[10px]">
                                    <span className="font-semibold text-slate-700">
                                      #{idx + 1} {src.id || 'chunk'}
                                    </span>
                                    {src.distance !== undefined && (
                                      <span className="text-[9px] bg-white px-1 rounded border">
                                        dist: {Number(src.distance).toFixed(3)}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-slate-600 mt-1 line-clamp-3 italic">
                                    "{src.snippet || src.document || 'Snippet context available.'}"
                                  </p>
                                  {src.metadata?.category && (
                                    <span className="inline-block mt-1 text-[9px] uppercase px-1.5 py-0.2 bg-blue-100/70 text-blue-700 rounded font-semibold">
                                      {src.metadata.category}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <span className="text-[10px] text-slate-400 mt-1 px-1">
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                );
              })}

              {/* Typing indicator */}
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 text-xs bg-white border border-slate-200 px-3.5 py-2 rounded-2xl rounded-bl-xs w-max shadow-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                  </div>
                  <span className="text-[11px] text-slate-500 font-medium">
                    Searching vector base & generating reply...
                  </span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Starter Prompt Chips (shown if only welcome message) */}
            {messages.length <= 2 && (
              <div className="px-4 py-2 border-t border-slate-100 bg-white/70">
                <p className="text-[11px] font-medium text-slate-500 mb-1.5">Suggested Questions:</p>
                <div className="flex flex-wrap gap-1.5">
                  {STARTER_PROMPTS.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(prompt)}
                      disabled={loading}
                      className="text-left text-[11px] bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 px-2.5 py-1 rounded-full border border-slate-200/80 transition-all truncate max-w-full"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input area */}
            <div className="p-3 bg-white border-t border-slate-100">
              <div className="flex items-center gap-2 bg-slate-50 rounded-2xl p-1.5 border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question about Businex..."
                  rows={1}
                  className="flex-1 bg-transparent resize-none text-[13px] px-2 py-1.5 text-slate-800 placeholder-slate-400 focus:outline-none max-h-24"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!input.trim() || loading}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-600 text-white rounded-xl transition-all shadow-sm shrink-0"
                  aria-label="Send message"
                >
                  <Send size={15} />
                </button>
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-400 px-1 pt-1.5">
                <span>Enter to send, Shift+Enter for new line</span>
                <span className="flex items-center gap-1">
                  <ShieldCheck size={11} className="text-emerald-600" />
                  Enterprise Secure
                </span>
              </div>
            </div>

            {/* History Modal Backdrop */}
            {showHistoryModal && (
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs z-30 flex flex-col justify-end p-2">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 max-h-[85%] flex flex-col">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={16} className="text-blue-600" />
                      <h4 className="font-semibold text-sm text-slate-800">Session History</h4>
                    </div>
                    <button
                      onClick={() => setShowHistoryModal(false)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto py-3 space-y-2 text-xs">
                    {loadingHistory ? (
                      <div className="text-center py-6 text-slate-400">Loading conversation history...</div>
                    ) : historyItems.length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        No prior saved conversation found in MSSQL for this session.
                      </div>
                    ) : (
                      historyItems.map((item, i) => (
                        <div key={item.id || i} className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                          <p className="font-semibold text-slate-700 mb-1">
                            Q: {item.userMessage}
                          </p>
                          <p className="text-slate-600 text-[11px] line-clamp-3">
                            A: {item.botMessage}
                          </p>
                          <span className="text-[9px] text-slate-400 mt-1 block">
                            {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 rounded-xl transition-colors mt-2"
                  >
                    Close History
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingAIAssistant;
